
import { computed, defineComponent, reactive, ref, watch, PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import LoadMoreButton from '@/components/load-more-button.vue'
import {
  HardwareModifyData,
  HardwareModifyLogService,
  QueryParams,
  computedDefaultSort
} from './hardware-modify-log.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import IPColumn from '@/views/dashboard/_components/ip-column/IPColumn.vue'
import dayjs from 'dayjs'
import ContentAdaptLayout from '@/views/dashboard/_components/content-adapt-layout/content-adapt-layout.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { FormType, ObjectFormItem, DateType } from '@/components/cmForm/types'
import { useParseTime } from '@/hooks'
export default defineComponent({
  components: {
    ExportDropdown,
    SearchFilterBar,
    LoadMoreButton,
    ContentAdaptLayout,
    IPColumn,
    RawDataDrawer
  },
  props: {
    currentTab: {
      type: String,
      default: 'hardware-modify-log'
    },
    /** 嵌入抽屉等场景：初始日期范围与 reset 基准，优先级高于路由 query */
    embedDefaults: {
      type: Object as PropType<{ dateRange: [string, string] } | null>,
      default: null
    }
  },
  setup (props) {
    const { t, locale } = useI18n()
    const route = useRoute()
    const injector = useReflectiveInjector([HardwareModifyLogService])
    const service = injector.get(HardwareModifyLogService)

    const changeTypeLabels = computed(() => [
      '',
      t('noticeManage.hardwareChangeCpu'),
      t('noticeManage.hardwareChangeMemorySize'),
      t('noticeManage.hardwareChangeMemoryInfo'),
      t('noticeManage.hardwareChangeDisk'),
      t('noticeManage.hardwareChangeMotherboard'),
      t('noticeManage.hardwareChangeGpu'),
      t('noticeManage.hardwareChangeNic'),
      t('noticeManage.hardwareChangeAudio'),
      t('noticeManage.hardwareChangeDisplay'),
    ])

    const isLoading = ref(false)
    const hasMore = ref(true)

    /** 与终端管理等页一致：URL 带 `start_time`/`end_time`（秒或毫秒时间戳）时初始化日期范围 */
    function dateRangeFromRouteQuery (): string[] | null {
      const qs = route.query.start_time
      const qe = route.query.end_time
      if (qs == null || qe == null || qs === '' || qe === '') return null
      const s = Number(qs)
      const e = Number(qe)
      if (!Number.isFinite(s) || !Number.isFinite(e)) return null
      const sSec = s > 1e12 ? Math.floor(s / 1000) : s
      const eSec = e > 1e12 ? Math.floor(e / 1000) : e
      return [
        dayjs.unix(sSec).format('YYYY-MM-DD'),
        dayjs.unix(eSec).format('YYYY-MM-DD')
      ]
    }

    const defaultDate =
      props.embedDefaults?.dateRange?.length === 2
        ? [...props.embedDefaults.dateRange]
        : (dateRangeFromRouteQuery() ?? [
            dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
            dayjs().format('YYYY-MM-DD')
          ])
    const [start_time, end_time] = useParseTime(defaultDate)

    const data = reactive<{
      total: number
      list: HardwareModifyData[],
      isShowRawData: boolean,
      currentRow: any
    }>({
      total: 0,
      list: [],
      isShowRawData: false,
      currentRow: {}
    })

    const queryParams = reactive<any>({
      key_word: '',
      start_time: start_time != null ? String(start_time) : undefined,
      end_time: end_time != null ? String(end_time) : undefined,
      page: 1,
      limit: 20,
      dateRange: defaultDate
    })

    const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
      void locale.value
      return {
        dateRange: {
          colOpt: { style: 'flex: 0 0 280px' },
          label: t('noticeManage.filterDateRange'),
          type: FormType.Date,
          dateType: DateType.DateRange,
          outSearchFiledFlag: true,
          events: {
            change: (val: any) => {
              if (val) {
                const [start, end] = useParseTime(val)
                queryParams.start_time = start != null ? String(start) : undefined
                queryParams.end_time = end != null ? String(end) : undefined
              } else {
                queryParams.start_time = undefined
                queryParams.end_time = undefined
              }
              getMessageList()
            },
          },
          dateOpt: {
            'value-format': 'YYYY-MM-DD',
            'start-placeholder': t('common.startDate'),
            'end-placeholder': t('common.endDate'),
            clearable: false,
          },
        },
      }
    })

    const defaultSort = computedDefaultSort(
      props.embedDefaults ? {} : route.query,
      queryParams as QueryParams
    )

    function getMessageList (isLoadMore: boolean = false) {
      if (!isLoadMore) {
        queryParams.page = 1
        data.list = []
      }

      isLoading.value = true
      service.getHardwareModifyList(queryParams).then(response => {
        if (queryParams.page === 1) {
          data.list = response.list || []
        } else {
          data.list = [...data.list, ...(response.list || [])]
        }
        data.total = response.total || 0
        hasMore.value = data.list.length < data.total
      }).finally(() => {
        isLoading.value = false
      })
    }

    function handleSearch (value: string) {
      queryParams.key_word = value
      queryParams.page = 1
      getMessageList()
    }

    function handleQuery (form: any) {
      Object.assign(queryParams, form)
      queryParams.page = 1
      getMessageList()
    }

    function handleReset () {
      queryParams.key_word = ''
      const resetRange = props.embedDefaults
        ? [...props.embedDefaults.dateRange]
        : [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]
      queryParams.dateRange = resetRange
      const [start, end] = useParseTime(resetRange)
      queryParams.start_time = start != null ? String(start) : undefined
      queryParams.end_time = end != null ? String(end) : undefined
      queryParams.page = 1
      getMessageList()
    }

    function loadMoreData () {
      if (!hasMore.value || isLoading.value) return
      queryParams.page = queryParams.page! + 1
      getMessageList(true)
    }

    watch(
      () => props.currentTab,
      (newV) => {
        if (newV === 'hardware-modify-log') {
          getMessageList()
        }
      },
      { immediate: true }
    )
    return {
      data,
      queryParams,
      filterFormColumns,
      isLoading,
      hasMore,
      defaultSort,
      changeTypeLabels,
      download (type: 0|1) {
        service.download(type, queryParams, t('noticeManage.exportFileHardwareModifyLog'))
      },
      handlerSort ({ prop, order }: { prop: string, order: string }) {
        if (order === 'ascending') {
          queryParams.asc = true
        } else {
          queryParams.asc = false
        }
        queryParams.sort = prop
        queryParams.page = 1
        getMessageList()
      },
      handleSearch,
      handleQuery,
      handleReset,
      loadMoreData,
      showRawData (row: any) {
        data.currentRow = row
        data.isShowRawData = true
      }
    }
  }
})
